import React, { useState, useEffect } from 'react';
import { 
  Cpu, 
  Layers, 
  Settings, 
  Terminal, 
  CheckCircle, 
  HelpCircle, 
  AlertTriangle, 
  FileText, 
  ArrowRight, 
  Plus, 
  Trash2, 
  Edit3, 
  Download, 
  Zap, 
  Coins, 
  Laptop, 
  Database, 
  Gauge, 
  PhoneCall, 
  Save, 
  Grid, 
  TrendingUp, 
  Inbox, 
  Clock, 
  Shield, 
  Eye, 
  Check, 
  Search, 
  SlidersHorizontal, 
  X, 
  CreditCard, 
  Receipt,
  Sparkles,
  BookOpen,
  DollarSign,
  Package,
  CheckSquare,
  RefreshCw,
  FolderOpen
} from 'lucide-react';
import { DigitalProduct } from '../data/nexusDigitalProducts';

interface NexusDigitalProductFactoryProps {
  products: DigitalProduct[];
  setProducts: React.Dispatch<React.SetStateAction<DigitalProduct[]>>;
  onAddLog: (module: string, action: string, status: 'SUCCESS' | 'WARNING' | 'DENIED' | 'PENDING_APPROVAL', details: string) => void;
  onAddTaskToWorkSuite?: (title: string, priority: 'URGENT' | 'HIGH' | 'MEDIUM' | 'LOW') => void;
  publicInquiries: any[];
  setPublicInquiries: React.Dispatch<React.SetStateAction<any[]>>;
}

export const NexusDigitalProductFactory: React.FC<NexusDigitalProductFactoryProps> = ({
  products,
  setProducts,
  onAddLog,
  onAddTaskToWorkSuite,
  publicInquiries,
  setPublicInquiries
}) => {
  const [adminTab, setAdminTab] = useState<'factory' | 'planner' | 'queue' | 'inbox'>('factory');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationType, setGenerationType] = useState<string>('Website Template');
  const [selectedIndustry, setSelectedIndustry] = useState<string>('Local Shop');
  const [generationProgress, setGenerationProgress] = useState(0);

  // Daily Generator Queue Settings
  const [queueConfig, setQueueConfig] = useState(() => {
    const saved = localStorage.getItem('nexus_factory_queue_config');
    return saved ? JSON.parse(saved) : {
      productsPerDay: 3,
      productType: 'ALL',
      industry: 'Restaurant / Takeaway',
      difficulty: 'Intermediate',
      priceRange: 'Commercial Choice',
      isManagedLaunchEligible: true,
      includeSlideDeck: true,
      includeLandingPage: true,
      includeWorkflow: true,
      includeCodePack: true,
      includeSocialContent: true,
      status: 'Generating Draft' as 'Waiting' | 'Generating Draft' | 'Draft Ready' | 'Needs Review' | 'Approved for Preview' | 'Published' | 'Paused' | 'Failed Safely'
    };
  });

  // Approval Queue for Generated Drafts
  const [draftQueue, setDraftQueue] = useState<any[]>(() => {
    const saved = localStorage.getItem('nexus_factory_drafts_queue');
    return saved ? JSON.parse(saved) : [];
  });

  // Simulated REST Endpoints logger
  const [apiLog, setApiLog] = useState<string[]>([]);

  const triggerApiLog = (method: string, route: string, payload?: any) => {
    const timestamp = new Date().toISOString().split('T')[1].slice(0, 8);
    const line = `[${timestamp}] REST: ${method} ${route} ${payload ? JSON.stringify(payload) : ''}`;
    setApiLog(prev => [line, ...prev].slice(0, 10));
  };

  useEffect(() => {
    localStorage.setItem('nexus_factory_queue_config', JSON.stringify(queueConfig));
  }, [queueConfig]);

  useEffect(() => {
    localStorage.setItem('nexus_factory_drafts_queue', JSON.stringify(draftQueue));
  }, [draftQueue]);

  // Simulated B2B API call
  const callPlaceholderAPI = async (method: 'GET' | 'POST', endpoint: string, body?: any) => {
    triggerApiLog(method, endpoint, body);
    onAddLog('Product Factory API', `REST trigger: ${method} ${endpoint}`, 'SUCCESS', `Dispatched standard gateway package.`);
    return { ok: true, timestamp: Date.now() };
  };

  // KPI Calculations
  const stats = React.useMemo(() => {
    const draftCount = draftQueue.length;
    const publishedCount = products.filter(p => p.productStatus !== 'Draft').length;
    
    // Website, App, Workflow count
    const websites = products.filter(p => p.productType === 'Ready-Made Website').length;
    const apps = products.filter(p => p.productType === 'Ready-Made Mobile App').length;
    const workflows = products.filter(p => ['Automation Workflow', 'Make.com Workflow', 'n8n Workflow'].includes(p.productType)).length;
    const codes = products.filter(p => p.productType === 'Code Pack').length;
    const decks = products.filter(p => p.productType === 'Social Media Pack' || p.tags.includes('Deck')).length;

    // Estimate Revenue Pipeline (Footnote: Estimated opportunity based on admin planning)
    const pipelineEstimate = products.reduce((acc, curr) => acc + (curr.setupPrice || 0), 0);

    return {
      draftCount,
      publishedCount,
      generatedThisWeek: 4 + draftCount,
      websites,
      apps,
      workflows,
      codes,
      decks,
      highestCategory: 'AI Assistant Workflow',
      managedLaunchCount: products.filter(p => p.setupPrice > 0).length,
      pipelineEstimate
    };
  }, [products, draftQueue]);

  // GENERATOR TEMPLATES DATABASE (BLUEPRINTS)
  const handleGenerateProduct = (type: string, industry: string) => {
    setIsGenerating(true);
    setGenerationProgress(10);
    callPlaceholderAPI('POST', `/api/admin/digital-product-factory/generate-${type.toLowerCase().replace(/[^a-z0-9]/g, '-')}`, { industry });

    // Stagger progress animation
    const interval = setInterval(() => {
      setGenerationProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          finalizeProductGeneration(type, industry);
          return 100;
        }
        return prev + 25;
      });
    }, 200);
  };

  const finalizeProductGeneration = (type: string, industry: string) => {
    setIsGenerating(false);
    
    // Create blueprints based on requested product type and industry
    const newDraftId = `draft-${Date.now()}`;
    const cleanIndustry = industry || 'Local Business';
    const isHealthApp = type === 'Ready-Made Mobile App' && cleanIndustry.includes('Healthcare');
    const isPublicSector = cleanIndustry === 'Government / Public Sector Supplier Pack';

    let title = `${cleanIndustry} ${type} blueprint`;
    let setupPrice = 399;
    let templatePrice = 99;
    let category = `${cleanIndustry} System`;
    const techStack = type.includes('Website') ? ['React', 'Vite', 'Tailwind'] : type.includes('App') ? ['React Native', 'Expo', 'Node.js'] : ['Make.com API', 'n8n JSON'];

    // Customize based on selectors
    if (type.includes('Website')) {
      title = `NexoraX ${cleanIndustry} Website Platform`;
      setupPrice = 499;
      templatePrice = 149;
    } else if (type.includes('Mobile App')) {
      title = `${cleanIndustry} Mobilised Scheduler App`;
      setupPrice = 699;
      templatePrice = 199;
    } else if (type.includes('Dashboard')) {
      title = `Universal ${cleanIndustry} B2B Dashboard`;
      setupPrice = 349;
      templatePrice = 79;
    } else if (type.includes('Workflow')) {
      title = `${cleanIndustry} CRM Ingress Workflow Pipeline`;
      setupPrice = 199;
      templatePrice = 49;
    } else if (type.includes('Code Pack')) {
      title = `Secure ${cleanIndustry} Node-Express Server Pack`;
      setupPrice = 149;
      templatePrice = 29;
    } else if (type.includes('Slide Deck')) {
      title = `Lead Pitch Deck: ${cleanIndustry} Transformation Plan`;
      setupPrice = 249;
      templatePrice = 39;
    } else {
      title = `Strategic ${cleanIndustry} Corporate Launch Bundle`;
      setupPrice = 599;
      templatePrice = 249;
    }

    const newDraft = {
      id: newDraftId,
      productName: title,
      productType: type.includes('App') ? 'Ready-Made Mobile App' : type.includes('Website') ? 'Ready-Made Website' : type.includes('Workflow') ? 'Automation Workflow' : type.includes('Dashboard') ? 'Dashboard Template' : type.includes('Slide Deck') ? 'Social Media Pack' : 'Nexus Bundle',
      category: category,
      shortDescription: `A high-conversion ready-made ${type.toLowerCase()} engineered specifically for the ${cleanIndustry} industry. Includes standard files, schemas, and optional human setup.`,
      fullDescription: `Dispatched from the Quantum Factory Node. This ready-to-test blueprint comes bundled with fully responsive screen frameworks, styled metrics cards, modular routing structures, and step-by-step developer checklists. ${isHealthApp ? "Please note: This application contains trackers. Note: This is not medical advice and does not diagnose or treat health conditions." : ""}`,
      previewImage: 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?auto=format&fit=crop&q=80&w=800',
      productScreenshots: [],
      includedFiles: ['src/App.tsx', 'vite.config.ts', 'src/types.ts', 'src/db/schema.ts'],
      techStack: techStack,
      integrations: ['Google Maps API', 'Stripe Secure Webhook', 'Make.com Ingress'],
      setupDifficulty: 'Intermediate' as any,
      deliveryMethod: 'Client Dashboard Setup' as any,
      licenceType: 'Business Use',
      setupPrice: setupPrice,
      productPrice: templatePrice,
      monthlySupportPrice: 29,
      paymentStatus: 'Request Invoice',
      checkoutStatus: 'Invoice Only',
      productStatus: 'Draft' as any,
      buildStatus: 'Template Ready',
      approvalStatus: 'Pending Director Signoff',
      tags: ['Quantum Draft', type, cleanIndustry],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      riskNotes: `Evaluate client domain DNS mapping prior to SSL generation. ${isPublicSector ? "Note: Public Sector packs must contain strict factual summaries; do not imply government endorsement." : ""}`,
      humanApprovalRequirements: 'Requires DNS verification and custom billing register setup.',
      problemSolved: `Automates customer interface and client bookings bottleneck inside local ${cleanIndustry} micro-enterprise setups.`
    };

    setDraftQueue(prev => [newDraft, ...prev]);
    onAddLog('Product Factory', `Draft blueprint generated successfully: "${title}"`, 'SUCCESS', `Queue status: Draft Ready for Admin review. Category: ${cleanIndustry}`);
  };

  // Approve draft to go live
  const handleApproveDraft = (draft: any) => {
    callPlaceholderAPI('POST', `/api/admin/digital-products/request/${draft.id}/status`, { status: 'Published' });
    const liveItem: DigitalProduct = {
      ...draft,
      productStatus: 'Template Ready', // Make it live
      approvalStatus: 'Approved & Active'
    };

    setProducts(prev => [liveItem, ...prev]);
    setDraftQueue(prev => prev.filter(d => d.id !== draft.id));
    onAddLog('Product Factory', `Draft Approved & Published Live: "${draft.productName}"`, 'SUCCESS', 'Registry updated.');
    alert(`DRAFT APPROVED!\n\nThe product "${draft.productName}" is now live in the Digital Products catalog!`);
  };

  const handleDiscardDraft = (id: string) => {
    setDraftQueue(prev => prev.filter(d => d.id !== id));
    onAddLog('Product Factory', `Discarded draft blueprint: ${id}`, 'WARNING', 'Removed from local database memory.');
  };

  return (
    <div className="bg-[#04040a]/90 border border-purple-500/30 rounded-2xl p-6 shadow-2xl relative space-y-6">
      
      {/* GLOWING HEADER */}
      <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-purple-500 via-[#e043f0] to-cyan-400 rounded-t-2xl" />
      
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <span className="text-[10px] bg-purple-950/40 text-purple-300 font-mono font-black uppercase tracking-[0.25em] border border-purple-800/40 px-2.5 py-0.5 rounded-md inline-block">
            NODE-18.5 SECRETARY CORE DIRECTIVE
          </span>
          <h2 className="text-xl md:text-2xl font-sans font-black text-white uppercase tracking-tight mt-1 flex items-center gap-1.5">
            <Cpu className="w-6 h-6 text-[#e840f3]" />
            NEXUS DIGITAL PRODUCT FACTORY
          </h2>
          <p className="text-xs text-zinc-400 normal-case mt-1 max-w-2xl">
            Autonomous drafting chamber for automated websites, app screens, Make/n8n workflows, slide decks, and code starter packs.
          </p>
        </div>

        {/* SUB-TABS SELECTOR */}
        <div className="flex bg-black/60 border border-zinc-900 rounded-xl p-1 font-mono text-[9px] font-black uppercase tracking-wider shrink-0">
          <button
            onClick={() => { setAdminTab('factory'); callPlaceholderAPI('GET', '/api/admin/digital-product-factory'); }}
            className={`px-3 py-2 rounded-lg cursor-pointer transition-colors ${adminTab === 'factory' ? 'bg-[#e840f3] text-white font-black' : 'text-zinc-500 hover:text-white'}`}
          >
            Product Factory
          </button>
          <button
            onClick={() => { setAdminTab('queue'); callPlaceholderAPI('GET', '/api/admin/digital-product-factory/queue'); }}
            className={`px-3 py-2 rounded-lg cursor-pointer transition-colors ${adminTab === 'queue' ? 'bg-[#e840f3] text-white font-black' : 'text-zinc-500 hover:text-white'}`}
          >
            Daily Queue Settings
          </button>
          <button
            onClick={() => { setAdminTab('planner'); callPlaceholderAPI('GET', '/api/admin/digital-product-factory/revenue-planner'); }}
            className={`px-3 py-2 rounded-lg cursor-pointer transition-colors ${adminTab === 'planner' ? 'bg-[#e840f3] text-white font-black' : 'text-zinc-500 hover:text-white'}`}
          >
            Revenue Planner
          </button>
          <button
            onClick={() => { setAdminTab('inbox'); }}
            className={`px-3 py-2 rounded-lg cursor-pointer transition-colors relative ${adminTab === 'inbox' ? 'bg-[#e840f3] text-white' : 'text-zinc-500 hover:text-white'}`}
          >
            Public Inbox
            {publicInquiries.length > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white font-sans font-black text-[8px] rounded-full w-4 h-4 flex items-center justify-center animate-pulse">
                {publicInquiries.length}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* KPI METRICS DECK */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-[#080816]/75 border border-zinc-900 rounded-xl p-4 text-center space-y-1 relative group hover:border-[#e840f3]/45 transition-colors">
          <span className="text-[9px] font-mono font-black text-rose-400 uppercase tracking-widest block">Drafts Review Queue</span>
          <span className="text-2xl font-bold font-sans text-rose-400 block">{stats.draftCount}</span>
          <span className="text-[8.5px] font-mono text-zinc-500 block">Needs Director signoff</span>
          <div className="absolute top-2 right-2 text-[8px] font-mono text-zinc-600 block group-hover:text-amber-500 transition-colors">★</div>
        </div>

        <div className="bg-[#080816]/75 border border-zinc-900 rounded-xl p-4 text-center space-y-1 hover:border-[#e840f3]/45 transition-colors">
          <span className="text-[9px] font-mono font-black text-purple-400 uppercase tracking-widest block">Published Live</span>
          <span className="text-2xl font-bold font-sans text-purple-400 block">{stats.publishedCount}</span>
          <span className="text-[8.5px] font-mono text-[#00ff00] block">⚡ Standard matrix</span>
        </div>

        <div className="bg-[#080816]/75 border border-zinc-900 rounded-xl p-4 text-center space-y-1 hover:border-cyan-500/50 transition-colors">
          <span className="text-[9px] font-mono font-black text-cyan-400 uppercase tracking-widest block">Highest Category</span>
          <span className="text-sm font-bold font-mono text-white block uppercase py-1 leading-none">{stats.highestCategory}</span>
          <span className="text-[8.5px] font-mono text-zinc-500 block">Lead Conversion Potential</span>
        </div>

        <div className="bg-[#080816]/75 border border-zinc-900 rounded-xl p-4 text-center space-y-1 hover:border-[#e5c158]/50 transition-colors">
          <span className="text-[9px] font-mono font-black text-[#e5c158] uppercase tracking-widest block">Est. Revenue Pipeline</span>
          <span className="text-2xl font-bold font-sans text-[#e5c158] block">£{stats.pipelineEstimate.toLocaleString()}</span>
          <span className="text-[8px] font-mono text-zinc-500 block italic leading-none">Estimated opportunity based on planning.</span>
        </div>
      </div>

      <hr className="border-zinc-900" />

      {/* RENDER ACTIVE ADMIN TAB */}
      {adminTab === 'factory' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            
            {/* MANUAL BUILD TRIGGER CARDS */}
            <div className="lg:col-span-1 bg-[#050612] border border-cyan-500/20 rounded-xl p-5 space-y-4">
              <span className="text-[10px] text-cyan-400 font-mono font-black uppercase tracking-wider block">
                ⚡ DISPATCH BLUEPRINT GENERATION
              </span>
              <p className="text-[10.5px] text-zinc-400 leading-normal font-sans">
                Select target industry niche and product template schema. Our generator compiles structured descriptions, code tree layouts, pricing indices, and safety rules as draft items.
              </p>

              <div className="space-y-3 font-mono text-xs text-slate-300">
                <div className="space-y-1.5">
                  <label className="text-purple-400 uppercase text-[9px] font-black tracking-widest">PRODUCT TYPE SCHEMA:</label>
                  <select
                    value={generationType}
                    onChange={(e) => setGenerationType(e.target.value)}
                    className="w-full bg-black border border-zinc-800 rounded p-2 text-white text-xs cursor-pointer focus:border-[#e840f3]/50"
                  >
                    <option value="Ready-Made Website">Ready-Made Website</option>
                    <option value="Ready-Made Mobile App">Ready-Made Mobile App</option>
                    <option value="AI Assistant Template">AI Assistant Template</option>
                    <option value="Dashboard Template">Dashboard Template</option>
                    <option value="Automation Workflow">AI Workflow Blueprint</option>
                    <option value="Code Pack">Source Code Pack</option>
                    <option value="Slide Deck">Slide Deck Template</option>
                    <option value="Business Pack">Business Operational Pack</option>
                  </select>
                </div>

                <div className="space-y-1.5">
                  <label className="text-purple-400 uppercase text-[9px] font-black tracking-widest">TARGET INDUSTRY NICHE:</label>
                  <select
                    value={selectedIndustry}
                    onChange={(e) => setSelectedIndustry(e.target.value)}
                    className="w-full bg-black border border-zinc-800 rounded p-2 text-white text-xs cursor-pointer focus:border-[#e840f3]/50"
                  >
                    <option value="Restaurant / Takeaway">Restaurant / Takeaway</option>
                    <option value="Driving School">Driving School</option>
                    <option value="Beauty / Salon">Beauty / Salon</option>
                    <option value="Trades / Construction">Trades / Construction</option>
                    <option value="Real Estate">Real Estate</option>
                    <option value="Healthcare / Wellness">Healthcare / Wellness</option>
                    <option value="SaaS Startup">SaaS Startup</option>
                    <option value="AI Startup">AI Startup</option>
                    <option value="Digital Creator">Digital Creator Pack</option>
                    <option value="Government / Public Sector Supplier Pack">Government Supplier Pack</option>
                  </select>
                </div>

                {isGenerating ? (
                  <div className="space-y-2 pt-2">
                    <div className="flex justify-between text-[10px] font-bold text-cyan-400">
                      <span>COMPILING ASSET TREE...</span>
                      <span>{generationProgress}%</span>
                    </div>
                    <div className="w-full bg-zinc-950 rounded-full h-1.5 overflow-hidden">
                      <div className="bg-gradient-to-r from-purple-500 to-cyan-400 h-1.5 transition-all duration-200" style={{ width: `${generationProgress}%` }} />
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => handleGenerateProduct(generationType, selectedIndustry)}
                    className="w-full py-2.5 bg-gradient-to-r from-purple-900 to-[#e840f3]/90 text-white font-black uppercase rounded-lg text-[10px] tracking-widest border border-[#e840f3]/30 hover:brightness-110 active:scale-[0.98] transition-all cursor-pointer flex items-center justify-center gap-1.5"
                  >
                    <Sparkles className="w-4 h-4 animate-pulse" />
                    <span>Generate Draft Blueprint</span>
                  </button>
                )}
              </div>
            </div>

            {/* DRAFT BLUEPRINT QUEUE GRID (Section 3 & 4-10 outputs) */}
            <div className="lg:col-span-2 bg-black/60 border border-zinc-900 rounded-xl p-5 space-y-4">
              <span className="text-[10px] text-purple-400 font-mono font-black uppercase tracking-wider block">
                ★ GENERATED DRAFT QUEUE (APPROVALS MATRIX)
              </span>

              {draftQueue.length === 0 ? (
                <div className="p-8 border border-dashed border-zinc-850 rounded-lg text-center text-zinc-500 text-xs font-mono">
                  <Inbox className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
                  <span>No draft blueprints currently in register. Use the generator on the left to spawn new item matrices.</span>
                </div>
              ) : (
                <div className="space-y-4 max-h-[360px] overflow-y-auto pr-1">
                  {draftQueue.map((draft) => (
                    <div key={draft.id} className="bg-[#040409] border border-zinc-900/80 rounded-lg p-4 font-mono space-y-3 border-l-2 border-l-pink-500">
                      
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="text-xs font-sans font-black text-[#e840f3] uppercase leading-none">{draft.productName}</h4>
                          <span className="text-[8.5px] text-zinc-500 block mt-1 uppercase">CATEGORY: {draft.category} | LICENSE: {draft.licenceType}</span>
                        </div>
                        
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleApproveDraft(draft)}
                            className="bg-emerald-600/25 border border-emerald-500 text-[#00ff00] text-[8.5px] font-black px-2.5 py-1 rounded cursor-pointer hover:bg-emerald-500 hover:text-black transition-all"
                          >
                            Approve &amp; Publish
                          </button>
                          <button
                            onClick={() => handleDiscardDraft(draft.id)}
                            className="bg-rose-950/20 border border-rose-900 text-rose-400 text-[8.5px] font-black px-2 py-1 rounded cursor-pointer hover:bg-rose-600 hover:text-white transition-all flex items-center justify-center"
                          >
                            <Trash2 size={11} />
                          </button>
                        </div>
                      </div>

                      <p className="text-[10.5px] text-zinc-400 leading-normal normal-case font-sans line-clamp-2">
                        {draft.shortDescription}
                      </p>

                      <div className="grid grid-cols-2 gap-3 text-[9px] bg-black/40 border border-zinc-950 p-2.5 rounded text-zinc-500">
                        <div>
                          <span className="block text-zinc-400 font-bold">🛠️ EXPECTED BUILD FILES:</span>
                          <span className="block italic leading-tight text-zinc-500">{draft.includedFiles.slice(0, 3).join(', ')}...</span>
                        </div>
                        <div>
                          <span className="block text-purple-400 font-black">💰 BILLING INDICATORS:</span>
                          <span className="block leading-none mt-1">Setup: <strong className="text-white">£{draft.setupPrice}</strong> | Template: <strong className="text-amber-500">£{draft.productPrice}</strong></span>
                        </div>
                      </div>

                      {draft.riskNotes && (
                        <div className="bg-amber-950/15 border border-amber-900/30 p-2 rounded text-[9.5px] text-amber-500 leading-normal flex gap-1.5 items-start">
                          <AlertTriangle className="w-3.5 h-3.5 shrink-0 text-amber-500 mt-0.5" />
                          <span><strong className="uppercase font-black block text-[8px]">Compliance risk parameter logged:</strong> {draft.riskNotes}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

          </div>
        </div>
      )}

      {/* DAILY QUEUE SETTINGS */}
      {adminTab === 'queue' && (
        <div className="bg-[#050612] border border-zinc-900 rounded-xl p-5 space-y-5 font-mono text-xs">
          <div className="flex items-center justify-between border-b border-zinc-900 pb-2">
            <span className="text-[10.5px] text-purple-400 font-black uppercase tracking-wider">
              ⚙️ NEXUS DAILY PRODUCT GENERATOR PIPELINE STATUS
            </span>
            <span className="text-[#00ff00] bg-emerald-500/10 border border-emerald-500/20 text-[9px] font-black px-2.5 py-0.5 rounded uppercase">
              ACTIVE POOL
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="space-y-1.5">
                <div className="flex justify-between font-black uppercase tracking-wide text-[9px] text-zinc-400">
                  <span>Draft Blueprints Per Day (Rate Limit):</span>
                  <span className="text-cyan-400">{queueConfig.productsPerDay} / day</span>
                </div>
                <input
                  type="range"
                  min="1"
                  max="5"
                  value={queueConfig.productsPerDay}
                  onChange={(e) => setQueueConfig({ ...queueConfig, productsPerDay: parseInt(e.target.value) || 2 })}
                  className="w-full bg-[#030308] accent-purple-500 h-1 rounded cursor-pointer"
                />
                <span className="text-[9px] text-zinc-600 block leading-tight italic normal-case">Default: compile 2-3 custom draft matrices per day securely based on current strategy.</span>
              </div>

              <div className="space-y-1.5">
                <span className="text-zinc-400 uppercase text-[9px] font-black tracking-widest block">Queue Execution Mode:</span>
                <div className="flex gap-2">
                  {['Generating Draft', 'Paused', 'Waiting'].map((statusOption) => (
                    <button
                      key={statusOption}
                      onClick={() => setQueueConfig({ ...queueConfig, status: statusOption as any })}
                      className={`px-3 py-1.5 border rounded cursor-pointer text-[10px] font-bold ${queueConfig.status === statusOption ? 'bg-purple-950/40 border-[#e840f3] text-[#e840f3]' : 'bg-black/30 border-zinc-900 text-zinc-500 hover:text-white'}`}
                    >
                      {statusOption}
                    </button>
                  ))}
                </div>
              </div>

              <div className="bg-black/40 border border-zinc-900/60 p-3 rounded-lg text-[10.5px] text-zinc-400 leading-normal space-y-1.5 normal-case font-sans">
                <p>⚙️ <strong className="text-purple-300 font-bold uppercase font-mono text-[9px]">Automation Rule:</strong> Generated products are drafts only. Human administrator approval is strictly required before public publishing. No automatic public release. Failed cycles report safely to support frequency.</p>
              </div>
            </div>

            <div className="space-y-3 bg-[#020205] p-4.5 rounded-lg border border-zinc-900">
              <span className="text-zinc-400 uppercase text-[9px] font-black tracking-widest block border-b border-zinc-900 pb-1.5">COMPILE STENCILS CHECKLIST:</span>
              
              <div className="grid grid-cols-2 gap-3 text-[10px] text-zinc-500 font-bold">
                <label className="flex items-center gap-2 cursor-pointer hover:text-white">
                  <input
                    type="checkbox"
                    checked={queueConfig.includeSlideDeck}
                    onChange={(e) => setQueueConfig({ ...queueConfig, includeSlideDeck: e.target.checked })}
                    className="accent-purple-500"
                  />
                  <span>Include Slide Deck Outline</span>
                </label>
                
                <label className="flex items-center gap-2 cursor-pointer hover:text-white">
                  <input
                    type="checkbox"
                    checked={queueConfig.includeLandingPage}
                    onChange={(e) => setQueueConfig({ ...queueConfig, includeLandingPage: e.checked })}
                    className="accent-purple-500"
                  />
                  <span>Landing Page Mock</span>
                </label>
                
                <label className="flex items-center gap-2 cursor-pointer hover:text-white">
                  <input
                    type="checkbox"
                    checked={queueConfig.includeWorkflow}
                    onChange={(e) => setQueueConfig({ ...queueConfig, includeWorkflow: e.target.checked })}
                    className="accent-purple-500"
                  />
                  <span>Make/Zapier Flow schema</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer hover:text-white">
                  <input
                    type="checkbox"
                    checked={queueConfig.includeCodePack}
                    onChange={(e) => setQueueConfig({ ...queueConfig, includeCodePack: e.target.checked })}
                    className="accent-purple-500"
                  />
                  <span>Starter Code Files Tree</span>
                </label>
              </div>

              <button
                onClick={() => {
                  onAddLog('Product Queue', 'Daily queue compilation triggers flushed.', 'SUCCESS', 'Reset background timers.');
                  alert("Daily generator queue sync configuration saved. Sprints will populate under the queue registry.");
                }}
                className="w-full mt-3 py-1.5 bg-zinc-950 border border-zinc-800 hover:border-cyan-500/40 rounded text-[9.5px] text-slate-300 transition-colors uppercase font-black"
              >
                Save Sprints Parameters
              </button>
            </div>
          </div>
        </div>
      )}

      {/* REVENUE PLANNER */}
      {adminTab === 'planner' && (
        <div className="space-y-4">
          <div className="font-mono text-xs bg-[#050612] border border-zinc-900 p-4.5 rounded-xl space-y-4">
            <div className="flex justify-between items-center border-b border-zinc-900 pb-2">
              <span className="text-[10px] text-yellow-500 font-bold uppercase tracking-wider block">
                💰 DIGITAL PRODUCT REVENUE &amp; COMMUNITY PLANNER
              </span>
              <span className="text-zinc-500 text-[9px] font-mono leading-none">Planning Phase v1.1</span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-[10px] font-mono">
                <thead>
                  <tr className="border-b border-zinc-900 text-zinc-500 uppercase">
                    <th className="py-2.5">Product Name</th>
                    <th className="py-2.5">Category</th>
                    <th className="py-2.5 text-right">Standard Setup</th>
                    <th className="py-2.5 text-right">Managed Launch Price</th>
                    <th className="py-2.5 text-right">M.Support</th>
                    <th className="py-2.5 text-right">Est.Monthly Sales</th>
                    <th className="py-2.5 text-right">Est.Setup Rev</th>
                    <th className="py-2.5 text-right">3% Comm. Eligible</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-900/60 text-slate-300">
                  {products.slice(0, 6).map((p) => {
                    const standardPrice = p.setupPrice || 399;
                    const managedLaunchPrice = Math.floor(standardPrice * 0.6); // 40% discount
                    const estSales = 4;
                    const setupRev = estSales * managedLaunchPrice;

                    return (
                      <tr key={p.id} className="hover:bg-zinc-950/40">
                        <td className="py-2.5 font-bold uppercase text-white truncate max-w-[150px]">{p.productName}</td>
                        <td className="py-2.5 text-zinc-500 truncate max-w-[100px]">{p.category}</td>
                        <td className="py-2.5 text-right font-bold text-slate-400">£{standardPrice}</td>
                        <td className="py-2.5 text-right font-black text-cyan-400">£{managedLaunchPrice}</td>
                        <td className="py-2.5 text-right text-purple-400">£{p.monthlySupportPrice || 29}/mo</td>
                        <td className="py-2.5 text-right text-zinc-500">{estSales} units</td>
                        <td className="py-2.5 text-right text-amber-500 font-bold">£{setupRev}</td>
                        <td className="py-2.5 text-right">
                          <span className="bg-emerald-500/10 text-emerald-400 border border-emerald-500/35 px-1.5 py-0.5 rounded text-[8.5px]">
                            Yes (3%)
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="bg-black/50 border border-zinc-950 p-3 rounded-lg text-[10px] text-zinc-400 leading-relaxed font-sans normal-case space-y-1 mt-2">
              <p>⚡ <strong className="font-mono text-[9px] uppercase font-black text-amber-500">Regulatory Fiscal Caveats:</strong> Estimated planning figures only. These represent internal opportunity targets and does not guarantee income, funding or instant market success.</p>
              <p>🤝 <strong className="font-mono text-[9px] uppercase font-black text-cyan-400">Community Allocation Mandate:</strong> Where enabled and documented, NexoraX may allocate 3% of eligible net receipts to approved community or charitable initiatives. Do not claim donations unless recorded and verifiable.</p>
            </div>
          </div>
        </div>
      )}

      {/* PUBLIC CUSTOM REQUEST INBOX (Section 12 details storage) */}
      {adminTab === 'inbox' && (
        <div className="space-y-4">
          <div className="font-mono text-xs bg-[#050612] border border-zinc-900 p-5 rounded-xl space-y-4">
            <span className="text-[10px] text-pink-400 font-mono font-black uppercase tracking-wider block">
              📬 PUBLIC CLIENT-REQUEST SERVICE INBOX
            </span>

            {publicInquiries.length === 0 ? (
              <div className="p-8 border border-dashed border-zinc-850 rounded-lg text-center text-zinc-500 text-xs font-mono">
                <Inbox className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
                <span>Zero incoming custom asset client requests. Public visitors can fill out the form to request a custom build.</span>
              </div>
            ) : (
              <div className="space-y-4 max-h-[350px] overflow-y-auto">
                {publicInquiries.map((inq: any) => (
                  <div key={inq.id} className="bg-[#030307] border border-zinc-900 rounded-lg p-4 font-mono space-y-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="text-xs font-sans font-black text-white hover:text-cyan-400 transition-colors uppercase">{inq.name} ({inq.businessName || 'Freelancer'})</h4>
                        <span className="text-[8.5px] text-zinc-500 block">Email: {inq.email} | Target Budget: <strong className="text-emerald-400">£{inq.budgetRange}</strong></span>
                      </div>
                      
                      <div className="flex items-center gap-1.5">
                        <span className="bg-[#e840f3]/10 text-[#e840f3] border border-[#e840f3]/25 text-[8.5px] uppercase font-bold px-2.5 py-0.5 rounded">
                          Incoming Custom-Setup Query
                        </span>
                        
                        <button
                          onClick={() => {
                            // Turn this into a real delivery setup
                            const deliveryMock = {
                              id: `del-${Date.now()}`,
                              productId: `custom-${Date.now()}`,
                              productName: `Custom: ${inq.productTypeNeeded} Built`,
                              previewImage: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=800',
                              type: inq.productTypeNeeded,
                              config: `Custom request with Managed Launch bundle: ${inq.needHosting ? "Yes" : "No"} | Slide Deck: ${inq.needSlideDeck ? "Yes" : "No"}`,
                              status: 'Quote Preparing',
                              checklist: [
                                { task: 'Dispatch Custom Quote & Business Blueprint Draft', done: true },
                                { task: 'Scribe client requirements', done: true },
                                { task: 'Accounting compliance invoice sign-off', done: false },
                                { task: 'Verify Hosting container capacity', done: false },
                                { task: 'Initiate Git repo setup and release files', done: false }
                              ],
                              files: ['Custom-Build-Package.zip'],
                              demoLink: '',
                              invoiceStatus: 'Invoice Prepared',
                              supportStatus: 'In review',
                              workSuiteId: `ws-req-${Date.now().toString().slice(-4)}`
                            };

                            setProducts(prev => {
                              // Register as draft in marketplace
                              const productDraft : DigitalProduct = {
                                id: `ndp-req-${Date.now()}`,
                                productName: `Custom: ${inq.productTypeNeeded} for ${inq.businessName}`,
                                productType: 'Ready-Made Website',
                                category: inq.industry,
                                shortDescription: `Bespoke digital solution: ${inq.message || "Custom business setup."}`,
                                fullDescription: `Individually structured based on public inquiry. Deliverables include optimized server files and customized business blueprints.`,
                                previewImage: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=800',
                                productScreenshots: [],
                                includedFiles: ['src/App.tsx', 'custom.ts'],
                                techStack: ['Custom Stack', 'React', 'Tailwind'],
                                integrations: [],
                                setupDifficulty: 'Done-For-You Recommended',
                                deliveryMethod: 'Client Dashboard Delivery',
                                licenceType: 'Extended Licence',
                                setupPrice: inq.budgetRange === 'Under £250' ? 249 : 499,
                                productPrice: inq.budgetRange === 'Under £250' ? 99 : 199,
                                monthlySupportPrice: 29,
                                paymentStatus: 'Request Invoice',
                                checkoutStatus: 'Invoice Only',
                                productStatus: 'Draft',
                                buildStatus: 'Setup Required',
                                approvalStatus: 'Admin Draft',
                                tags: ['Bespoke', inq.industry],
                                featured: false,
                                createdAt: new Date().toISOString(),
                                updatedAt: new Date().toISOString()
                              };
                              return [productDraft, ...prev];
                            });

                            alert("Handshake confirmed. Proposal is drafted on Client Delivery Tracker.");
                            setPublicInquiries(prev => prev.filter(i => i.id !== inq.id));
                            onAddLog('Public Inbox', `Custom Inquiry accepted & draft quotation logged for "${inq.businessName}"`, 'SUCCESS', 'Moved to dashboard workflow.');
                          }}
                          className="bg-purple-600/20 border border-purple-500 font-black text-[9px] hover:bg-purple-600 text-[#e840f3] py-1 px-2.5 rounded cursor-pointer uppercase transition-colors"
                        >
                          Scribe Quote &amp; Delivery Track
                        </button>
                      </div>
                    </div>

                    <p className="text-[10.5px] text-zinc-300 normal-case leading-normal font-sans text-stone-200">
                      &ldquo;{inq.message || 'No additional details provided.'}&rdquo;
                    </p>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[8.5px] text-zinc-500">
                      <div>Need Setup Bundle: <strong className="text-white">{inq.needHosting ? "YES (Managed Launch)" : "No (Template only)"}</strong></div>
                      <div>Domains / Mail: <strong className="text-white">{inq.needDomainEmail ? "YES" : "No"}</strong></div>
                      <div>Workflow AI: <strong className="text-white">{inq.needAutomationWorkflow ? "YES" : "No"}</strong></div>
                      <div>Slide Deck: <strong className="text-white">{inq.needSlideDeck ? "YES" : "No"}</strong></div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* SIMULATED API LOG OUTLET */}
      <div className="bg-[#020205] border border-zinc-900 rounded-xl p-3 space-y-1 font-mono text-[8.5px]">
        <div className="flex justify-between items-center text-[7.5px] text-zinc-600 uppercase font-black tracking-widest pb-1 border-b border-zinc-900">
          <span>📡 SYSTEM GATEWAY: MOCK EXPRESS CONTROLLER ENDPOINTS</span>
          <span className="w-2 h-2 rounded-full bg-cyan-400 animate-ping" />
        </div>
        <div className="space-y-0.5 max-h-[80px] overflow-y-auto pt-1 text-slate-500">
          {apiLog.length === 0 ? (
            <p className="italic text-zinc-700">Awaiting integration requests from the public matrix...</p>
          ) : (
            apiLog.map((log, idx) => <p key={idx}>{log}</p>)
          )}
        </div>
      </div>

    </div>
  );
};
